import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import { colorFor, initialsFor } from './memberRoles';

/**
 * What this cool-down is expected to absorb.
 *
 * The reason this component exists: `cooldown` was a cycle status with nothing
 * attached to it, so the two weeks between cycles looked identical on the board to
 * the six before them. Putting the queue of triaged bugs at the top of a cool-down
 * cycle is what turns that status into instructions.
 *
 * Claim and fix are inline for the same reason. A cool-down is short, and making
 * someone leave the board to say they picked something up is how a list stops being
 * used.
 */
const CooldownBugPanel = ({
  bugs = [],
  currentUserId = null,
  canEdit = false,
  busyId = null,
  onClaim,
  onFix,
}) => {
  const [open, setOpen] = useState(true);

  if (!bugs.length) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2">Cool-down</Typography>
        <Typography variant="body2" color="text.secondary">
          No bugs queued for this one. Anything triaged as “fix in cool-down” shows
          up here.{' '}
          <Link component={RouterLink} to="/bugs">Open the pool</Link>
        </Typography>
      </Paper>
    );
  }

  const mine = bugs.filter(bug => bug.assigneeUserId === currentUserId);
  const unclaimed = bugs.filter(bug => !bug.assigneeUserId);

  return (
    <Paper variant="outlined" sx={{ mb: 3 }}>
      <Box
        sx={{
          px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1,
          cursor: 'pointer',
        }}
        onClick={() => setOpen(o => !o)}
      >
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle2">
            Cool-down: {bugs.length} bug{bugs.length === 1 ? '' : 's'} to absorb
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {unclaimed.length
              ? `${unclaimed.length} unclaimed`
              : 'All claimed'}
            {mine.length ? ` · ${mine.length} yours` : ''}
          </Typography>
        </Box>
        <Link component={RouterLink} to="/bugs" variant="caption" onClick={(e) => e.stopPropagation()}>
          The whole pool
        </Link>
        <IconButton size="small">
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={open}>
        <List dense disablePadding>
          {bugs.map(bug => (
            <ListItem
              key={bug.id}
              divider
              secondaryAction={canEdit ? (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {!bug.assigneeUserId && (
                    <Tooltip title="Take this one">
                      <IconButton
                        size="small"
                        disabled={busyId === bug.id}
                        onClick={() => onClaim?.(bug)}
                      >
                        <PersonAddAltIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Button
                    size="small"
                    startIcon={<CheckCircleOutlineIcon fontSize="small" />}
                    disabled={busyId === bug.id}
                    onClick={() => onFix?.(bug)}
                  >
                    Fixed
                  </Button>
                </Stack>
              ) : null}
            >
              {bug.assigneeUserId && (
                <Tooltip title={bug.assigneeName || bug.assigneeEmail || 'Assigned'}>
                  <Avatar
                    sx={{
                      width: 24, height: 24, fontSize: 11, mr: 1.5,
                      bgcolor: colorFor(bug.assigneeUserId),
                    }}
                  >
                    {initialsFor(bug.assigneeName || bug.assigneeEmail)}
                  </Avatar>
                </Tooltip>
              )}
              <ListItemText
                primary={(
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="body2">{bug.title}</Typography>
                    {bug.reportCount > 1 && (
                      <Chip size="small" variant="outlined" color="warning" label={`Seen ${bug.reportCount}×`} sx={{ height: 18, fontSize: 10 }} />
                    )}
                    {bug.jiraKey && (
                      <Link
                        href={bug.jiraUrl}
                        target="_blank"
                        rel="noopener"
                        variant="caption"
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
                      >
                        {bug.jiraKey}
                        <OpenInNewIcon sx={{ fontSize: 11 }} />
                      </Link>
                    )}
                  </Stack>
                )}
              />
            </ListItem>
          ))}
        </List>
      </Collapse>
    </Paper>
  );
};

export default CooldownBugPanel;
